// 그물 덫 속박 표시(c022 라이라): 속박된 몹(GameView.mobs.rooted > 0) 발밑에 청록 그물 고리를 깐다.
// 드로우콜 1(InstancedMesh 한 개, 가산 혼합), 몹 용량만큼 미리 만든다. 프레임마다 행렬·색 값만 바꾼다(할당 없음).
// 고리는 천천히 돌고 조여드는 맥동을 하며, 속박이 끝나기 0.4초 전부터 옅어진다. 섬광 없음.
import * as THREE from 'three/webgpu';
import { abs, float, fract, mix, smoothstep, uv, vec3 } from 'three/tsl';
import { MOB_STATS } from '../shared/config';
import { MobState, type GameView } from '../shared/types';

/** 사라지기 시작하는 남은 틱(0.4초) */
export const ROOT_FADE_TICKS = 24;
const TEAL = new THREE.Color(0x3fd0c0);

/** 그물 고리 판: 가는 바깥 고리 + 안쪽 마름모 그물 줄(uv 무늬). 크기 1(지름 1m) */
function netMaterial(): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
  const p = uv().sub(0.5).mul(2);
  const r = p.length();
  // 바깥 고리(반경 0.86~0.98) + 그물 격자(45° 기울인 줄)
  const ring = smoothstep(0.8, 0.88, r).mul(float(1).sub(smoothstep(0.94, 1.0, r)));
  const gx = abs(fract(p.x.add(p.y).mul(2.5)).sub(0.5)), gy = abs(fract(p.x.sub(p.y).mul(2.5)).sub(0.5));
  const net = float(1).sub(smoothstep(0.03, 0.09, gx.min(gy))).mul(float(1).sub(smoothstep(0.78, 0.86, r))).mul(0.55);
  const a = ring.add(net).clamp(0, 1);
  m.colorNode = mix(vec3(TEAL.r, TEAL.g, TEAL.b), vec3(0.85, 0.95, 1.0), ring.mul(0.5)).mul(1.4);
  m.opacityNode = a;
  return m;
}

export class RootRings {
  readonly mesh: THREE.InstancedMesh;
  private readonly mtx = new THREE.Matrix4();
  private readonly q = new THREE.Quaternion();
  private readonly pos = new THREE.Vector3();
  private readonly scl = new THREE.Vector3();
  private readonly col = new THREE.Color();
  private readonly flat = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), -Math.PI / 2);
  private readonly spin = new THREE.Quaternion();
  private readonly yAxis = new THREE.Vector3(0, 1, 0);
  private gain = 1;

  constructor(capacity: number) {
    this.mesh = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), netMaterial(), Math.max(1, capacity));
    this.mesh.name = 'fx-root-rings';
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 4;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.col.setRGB(1, 1, 1);
    for (let i = 0; i < Math.max(1, capacity); i++) this.mesh.setColorAt(i, this.col);
    this.mesh.instanceColor!.setUsage(THREE.DynamicDrawUsage);
    this.mesh.visible = false;
  }

  get drawCalls(): number { return this.mesh.count > 0 ? 1 : 0; }

  setReduceFlash(on: boolean): void { this.gain = on ? 0.7 : 1; }

  /** timeSec = 세계 시계(초). 속박 몹마다 발밑 고리 하나 */
  update(view: GameView, timeSec: number): void {
    const m = view.mobs, rooted = m.rooted;
    let n = 0;
    if (rooted) {
      const cap = Math.min(m.capacity, this.mesh.instanceMatrix.count);
      for (let i = 0; i < cap; i++) {
        const t = rooted[i];
        if (t <= 0) continue;
        const st = m.state[i];
        if (st === MobState.Inactive || st === MobState.Dying) continue;
        const k = MOB_STATS[m.kind[i]];
        const r = (k ? k.radius : 0.6) * (m.elite[i] ? 1.4 : 1);
        const pulse = 1 + 0.06 * Math.sin(timeSec * 9 + i);
        const d = r * 2.4 * pulse;
        this.spin.setFromAxisAngle(this.yAxis, timeSec * 0.8 + i * 0.7);
        this.q.copy(this.spin).multiply(this.flat);
        this.pos.set(m.x[i], 0.04, m.z[i]);
        this.scl.set(d, d, d);
        this.mtx.compose(this.pos, this.q, this.scl);
        this.mesh.setMatrixAt(n, this.mtx);
        const fade = Math.min(1, t / ROOT_FADE_TICKS) * this.gain;
        this.col.setRGB(fade, fade, fade);
        this.mesh.setColorAt(n, this.col);
        n++;
      }
    }
    this.mesh.count = n;
    this.mesh.visible = n > 0;
    if (n > 0) {
      this.mesh.instanceMatrix.needsUpdate = true;
      this.mesh.instanceColor!.needsUpdate = true;
    }
  }

  clear(): void { this.mesh.count = 0; this.mesh.visible = false; }

  prewarm(on: boolean): void {
    if (on) { this.mesh.visible = true; this.mesh.count = 1; }
    else this.clear();
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.mesh.dispose();
  }
}
